import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InsightCard } from '../molecules/InsightCard.js';
import { Typography } from '../atoms/Typography.js';
import type { Discrepancy } from '../../types/survey.js';

interface DiscrepancyListProps {
  discrepancies: Discrepancy[];
  className?: string;
}

type SeverityFilter = 'all' | 'high' | 'medium' | 'low';

export function DiscrepancyList({ discrepancies, className = '' }: DiscrepancyListProps) {
  const { t } = useTranslation('dashboard');
  const [filter, setFilter] = useState<SeverityFilter>('all');

  const filtered =
    filter === 'all' ? discrepancies : discrepancies.filter((d) => d.severity === filter);

  const sorted = [...filtered].sort((a, b) => {
    const order: Record<string, number> = { high: 0, medium: 1, low: 2 };
    return (order[a.severity] ?? 3) - (order[b.severity] ?? 3);
  });

  return (
    <div className={className}>
      <div className="mb-4 flex items-center justify-between">
        <Typography variant="h4">{t('discrepancies.title')}</Typography>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as SeverityFilter)}
          className="rounded-lg border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="all">{t('discrepancies.filter_all')}</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      {sorted.length === 0 ? (
        <Typography variant="body-sm" className="text-center py-8">
          {t('discrepancies.empty')}
        </Typography>
      ) : (
        <div className="flex flex-col gap-3">
          {sorted.map((d) => (
            <InsightCard
              key={d.id}
              title={d.type}
              description={d.description}
              severity={d.severity}
            />
          ))}
        </div>
      )}
    </div>
  );
}
